// @flow
import * as routes from '~/core/constants/routes';

import { acquireDevices, finishedInstallDevices, startAcquireDevices, stopAcquireDevices } from './actions';
import { alerts, feedback } from '~/core/constants';
import { all, call, cancel, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { dashboardAddProduct, saveDashboardEdit, redirectToPostinstallGroups } from '~/core/state/dashboard/actions';
import { delay } from 'redux-saga';
import { devices as devicesApi, nodes as nodesApi } from '~/core/api';
import { errorsSelector, getAcquiredDevices, getPlacementAdvice } from './selectors';
import { getHub, getDeviceItems, getHubHasCapability } from '~/core/state/devices/selectors';
import {
    FINISH_INSTALL_DEVICES,
    FINISHED_INSTALL_DEVICES,
    LOAD_INSTALL_DEVICES,
    PLACEMENT_ADVICE_TYPES,
    STOP_ACQUIRE_DEVICES,
    UNLOAD_INSTALL_DEVICES
} from './constants';

import { compose, find, uniqBy, sortBy, flowRight } from 'lodash/fp';
import { deviceTypes, capabilities, bulbTypes } from '~/core/constants/devices';
import type { FinishInstallDevicesAction } from './types';
import { getConfig } from '~/core/state/config/selectors';
import { getHive2UpgradeRouteState } from '~/core/state/install-upgrade-thermostat/hive2/selectors';
import { upgradeRouteState } from '~/core/state/install-upgrade-thermostat/slt3-slt3b/selectors';
import { getProductItems } from '~/core/state/products/selectors';
import { getProducts } from '~/core/state/products/actions';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import { logOut } from '~/core/state/application/actions';
import messages from './intl';
import moment from 'moment-timezone';
import { navigate } from '~/core/state/navigate';
import { RECEIVE_PRODUCTS } from '~/core/state/products/constants';
import { receiveDevices } from '~/core/state/devices/actions';
import { requestAlert } from '~/core/state/alerts/actions';

const putRequestAlert = compose(put, requestAlert);

function* handleError(error, attemptsBeforeTroubleshooting, attemptsBeforeWarning) {
    yield put(acquireDevices(new Error(error)));

    const errors = yield select(errorsSelector);

    if (errors >= attemptsBeforeTroubleshooting) {
        throw error;
    } else {
        if (errors >= attemptsBeforeWarning) {
            yield putRequestAlert(messages.warning, alerts.types.WARNING);
        }
    }
}

export function* acquireDevicesSaga(): Generator<Effect, *, *> {
    const setDiscoveryMode = yield call(shouldSetDiscoveryMode);
    let hub;
    try {
        hub = yield call(findHub);
    } catch (err) {
        yield putRequestAlert(messages.startHubDiscoveryFail, alerts.types.ERROR);
        return yield put(navigate('/install'));
    }

    const attemptsBeforeWarning = yield select(getConfig, 'acquireDevices:attemptsBeforeWarning');
    const attemptsBeforeTroubleshooting = yield select(getConfig, 'acquireDevices:attemptsBeforeTroubleshooting');
    const hive2UpgradeRouteState = yield select(getHive2UpgradeRouteState);
    const slt3Slt3bUpgradeRouteState = yield select(upgradeRouteState);

    const pollExpiry =
        hive2UpgradeRouteState || slt3Slt3bUpgradeRouteState
            ? yield select(getConfig, 'upgradeThermostat:pollExpiry')
            : yield select(getConfig, 'acquireDevices:pollExpiry');
    const pollInterval = yield select(getConfig, 'acquireDevices:pollInterval');

    const startAcquireDevicesAction = startAcquireDevices();
    yield put(startAcquireDevicesAction);
    // If the user doesn't want to put the hub into discovery mode then
    // we can look for all devices so that we get something back at least.
    const startTime = setDiscoveryMode ? moment(startAcquireDevicesAction.payload) : moment(0);
    const finishTime = moment(startAcquireDevicesAction.payload).add(pollExpiry, 'ms');
    let timedOut = false;
    let nextInterval = pollInterval;
    const pollIntervalAsSeconds = pollInterval / 1000;

    try {
        do {
            const now = moment();
            if (now.isSameOrAfter(finishTime)) {
                timedOut = true;
                break;
            }

            const timeRemaining = finishTime.diff(now, 'seconds');
            if (timeRemaining < pollIntervalAsSeconds) {
                nextInterval = timeRemaining * 1000;
            }

            let patchNodeError;

            if (setDiscoveryMode) {
                [, patchNodeError] = yield call(
                    handleApi(nodesApi.patchNode),
                    { discovery: true },
                    deviceTypes.HUB,
                    hub.id
                );

                if (patchNodeError) {
                    yield* handleError(patchNodeError, attemptsBeforeTroubleshooting, attemptsBeforeWarning);
                }
            }

            if (!patchNodeError) {
                const [devices, devicesError] = yield call(handleApi(devicesApi.getDevices), startTime.toDate());

                if (devicesError) {
                    yield* handleError(devicesError, attemptsBeforeTroubleshooting, attemptsBeforeWarning);
                }

                yield put(acquireDevices(devices));
            }
        } while (yield call(delay, nextInterval));
    } catch (error) {
        if (hive2UpgradeRouteState) {
            yield put(navigate(`${routes.installDevicesTroubleshootingClean}/hive2`));
        } else {
            yield put(navigate(routes.installDevicesTroubleshootingClean));
        }
    } finally {
        if (setDiscoveryMode) {
            const [, error] = yield call(handleApi(nodesApi.patchNode), { discovery: false }, deviceTypes.HUB, hub.id);

            if (error) {
                yield putRequestAlert(messages.endHubDiscoveryFail, alerts.types.WARNING);
            }
        }

        if (timedOut) {
            const acquiredDevices = yield select(getAcquiredDevices);

            if (hive2UpgradeRouteState) {
                yield put(navigate(`${routes.installDevicesTroubleshootingClean}/hive2`));
            } else if (!acquiredDevices.length) {
                yield put(navigate(routes.installDevicesTroubleshootingClean));
            }
        }

        yield put(stopAcquireDevices());
    }
}

export function* shouldSetDiscoveryMode(): Generator<Effect, *, *> {
    const confirmHubDiscovery = yield select(getConfig, 'confirmHubDiscovery');

    return confirmHubDiscovery
        ? yield call(global.confirm, 'Are you sure you want to set the hub into discovery mode?')
        : true;
}

export function* findHub(): Generator<Effect, *, *> {
    const hub = yield select(getHub);

    if (hub) {
        return yield hub;
    }

    const [devices, error] = yield call(handleApi(devicesApi.getDevices));

    if (error) {
        throw error;
    }

    if (devices.length) {
        return yield find(d => d.type === deviceTypes.HUB, devices);
    }
}

export const watchLoadInstallDevices = function*(): Generator<Effect, *, *> {
    while (yield take(LOAD_INSTALL_DEVICES)) {
        const acquireDevicesTask = yield fork(acquireDevicesSaga);

        const action = yield take([
            FINISHED_INSTALL_DEVICES,
            STOP_ACQUIRE_DEVICES,
            UNLOAD_INSTALL_DEVICES,
            logOut.toString()
        ]);

        yield cancel(acquireDevicesTask);

        const hub = yield call(findHub);

        const shouldPatch = action.type !== logOut.toString();

        if (hub && shouldPatch) {
            yield call(handleApi(nodesApi.patchNode), { discovery: false }, deviceTypes.HUB, hub.id);
        }
    }
};

export function* finishInstallDevicesSaga({
    payload: devicesWithNames
}: FinishInstallDevicesAction): Generator<Array<*> | Effect, *, *> {
    const responses = yield all(
        devicesWithNames.map(({ device, newName }) =>
            call(handleApi(nodesApi.patchNode), { name: newName }, device.type, device.id)
        )
    );

    const err = find(([, error]) => error, responses);

    if (err) {
        yield putRequestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR);
        yield put(finishedInstallDevices(err));
        yield put(navigate(routes.dashboard));
        return;
    }

    //Reduce newly found devices directly into the store
    const oldDevices = yield select(getDeviceItems);
    const newDevices = yield select(getAcquiredDevices);
    const allNewDevices = newDevices.map(newDevice => {
        const [renamedDevice] = devicesWithNames.filter(({ device: { id } }) => id === newDevice.id);

        if (renamedDevice && (renamedDevice.newName || '').trim()) {
            const { device: { state, ...renamedRest }, newName } = renamedDevice;
            return {
                ...renamedRest,
                state: {
                    ...state,
                    name: newName
                }
            };
        }

        return newDevice;
    });

    const allDevices = flowRight(sortBy(a => a.sortOrder), uniqBy(d => d.id))([...oldDevices, ...allNewDevices]);

    yield put(finishedInstallDevices());
    yield put(receiveDevices(allDevices));

    yield put(getProducts());
    yield take(RECEIVE_PRODUCTS);

    // Add new products to dashboard cutomisation
    const products = yield select(getProductItems);
    const newProducts = products.filter(p => newDevices.some(d => [p.get('id'), p.get('parent')].includes(d.id)));

    for (const p of newProducts) {
        yield put(dashboardAddProduct(p.get('id')));
    }

    yield put(saveDashboardEdit());

    const newBulbItems = newDevices.filter(d => bulbTypes.includes(d.type));
    const deviceGroupsEnabled = yield select(getConfig, 'features:deviceGroups');
    const hubSupportsGroups = yield select(getHubHasCapability, capabilities.PRODUCT_GROUP);
    const redirectConditionMap = new Map([
        [true, null],
        [hubSupportsGroups && deviceGroupsEnabled && newBulbItems.length > 1, { action: redirectToPostinstallGroups }]
    ]);
    const redirectCondition = redirectConditionMap.get(true);
    if (redirectCondition) {
        yield put(redirectCondition.action(true));
    }

    const placementAdvice = yield select(getPlacementAdvice);
    if (placementAdvice === PLACEMENT_ADVICE_TYPES.NOT_SHOWN) {
        yield put(navigate(routes.dashboard));
    } else {
        yield put(navigate(routes.installDevicePlacement));
    }
}

function* watchFinishInstallDevices(): Generator<Effect, *, *> {
    yield takeEvery(FINISH_INSTALL_DEVICES, handleSaga(finishInstallDevicesSaga));
}

export default [watchLoadInstallDevices, watchFinishInstallDevices];



// WEBPACK FOOTER //
// ./src/core/state/acquire-devices/sagas.js